import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { Icons } from 'utoniq-ui'

import { PreSaleLotteryDetailFragment } from '@/clients/utoniq-core/schema'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { setPreference } from '@/core/native/storage'

type MembershipCellProps = {
  preSale: PreSaleLotteryDetailFragment
}
export const MembershipCell: Cell<MembershipCellProps> = ({ preSale }) => {
  const { t } = useTranslation('lotteryDetail')
  const { routerPages, generatePath } = useRouter()

  /** 先行販売に必要なメンバーシップのプラン */
  const membershipPlans = useMemo(() => {
    // 登録プランをすべて表示する
    const planIds = preSale?.planId
      ? [preSale.planId]
      : preSale?.purchesablePlanIds || []
    if (!planIds.length) return []
    const planData =
      preSale.artist?.subscription?.products?.filter(products =>
        products.plans.some(plan => planIds.includes(plan.planId))
      ) || []
    // 全プラン対象の場合はプラン詳細を表示しない
    return planData.length === preSale.artist?.subscription?.products?.length
      ? []
      : planData
  }, [preSale?.artist?.patron?.id])

  /** ユーザーの加入しているメンバーシップのプラン */
  const currentPatronProduct = useMemo(() => {
    const planId = preSale?.artist?.patron?.subscription?.planId
    if (!planId) return null

    return preSale.artist.subscription?.products?.find(products =>
      products.plans.find(plan => plan.planId === planId)
    )
  }, [preSale?.artist?.patron?.id])

  if (
    !preSale.isPremium &&
    (!preSale.purchesablePlanIds || preSale.purchesablePlanIds.length === 0)
  )
    return null

  return (
    <CellFrame sx={{ pt: 3, pb: 6 }}>
      <Typography
        variant="h6"
        sx={{ bgcolor: 'grey.50', px: 2, py: 1 }}
        color="text.secondary"
      >
        {t('condition', '応募条件')}
      </Typography>

      <Stack sx={{ pt: 5 }}>
        <Stack direction="row" spacing={0} sx={{ placeItems: 'center', pb: 2 }}>
          <Icons.MembersOnly fontSize="small" color="primary" />
          <Typography variant="subtitle2" color="text.hint">
            {t('isPremium')}
          </Typography>
        </Stack>
        <Typography variant="h6">
          {/* 年額の場合はAPIの返却値で プラン名 ＋ ”年額のみ” という文言が返ってくるため、planTitleのみを表示する */}
          {preSale.plans.map(plan => plan?.planTitle).join('\n')}
          {t('membershipLimited', '会員の方')}
        </Typography>
      </Stack>

      <Card variant="outlined" sx={{ p: 5, mt: 3 }}>
        <CardContent sx={{ pb: '0!important', p: 0 }}>
          {preSale.isAccessible ? (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1" sx={{ minWidth: '60px' }}>
                    {t('planTitle')}
                  </Typography>
                  <Typography variant="body2">
                    {currentPatronProduct?.title}
                  </Typography>
                </Stack>
                {!!preSale.isAccessible && (
                  <Chip
                    label={
                      <Typography variant="overline" color="white">
                        {t('joined')}
                      </Typography>
                    }
                    sx={{
                      height: 16,
                      bgcolor: 'primary.main',
                      borderRadius: '2px',
                      padding: '2px 4px',
                      '.MuiChip-label': { px: 0 },
                    }}
                  />
                )}
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 3, pb: 0 }}
              >
                <Typography variant="subtitle1" sx={{ minWidth: '60px' }}>
                  {t('updatedAt')}
                </Typography>
                <Typography variant="body2">
                  {preSale.artist.patron?.formattedUpdatedAt}
                </Typography>
                <Box sx={{ flex: 1 }} />
              </Stack>
            </>
          ) : (
            <>
              <Typography variant="subtitle1">
                {t('membershipBenefit')}
              </Typography>
              {membershipPlans.map(plan => (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    {plan.title}
                  </Typography>
                  <Typography variant="body2">{plan.content}</Typography>
                </>
              ))}
              <Box sx={{ display: 'grid', placeItems: 'cen' }}>
                <Button
                  sx={{ mt: 6, width: '212px', mx: 'auto' }}
                  size="large"
                  href={generatePath(routerPages.ArtistMembership, {
                    artistId: preSale?.artist?.id ?? '#',
                  })}
                  onClick={() => {
                    /**
                     * TODO:
                     * 年間メンバーシッププラン機能に伴う導線実装が完了したら、先行販売に戻るUIを下記ローカルストレージキーをもとに実装すること
                     * https://zpl.io/kKOkROg
                     */
                    setPreference('currentPreSaleId', {
                      preSaleId: preSale.id,
                    })
                  }}
                >
                  {t('seeDetail')}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </CellFrame>
  )
}
